/**
 * Normalize a string for comparison by converting to lowercase,
 * removing special characters, and trimming whitespace
 */
export function normalizeString (str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Calculate Levenshtein distance between two strings
 */
export function levenshteinDistance (a: string, b: string): number {
  const matrix: number[][] = []

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i]
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1]
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        )
      }
    }
  }

  return matrix[b.length][a.length]
}

/**
 * Calculate similarity score between two strings (0 to 1)
 */
export function stringSimilarity (a: string, b: string): number {
  const normalizedA = normalizeString(a)
  const normalizedB = normalizeString(b)

  if (normalizedA === normalizedB) {
    return 1
  }

  const maxLength = Math.max(normalizedA.length, normalizedB.length)
  if (maxLength === 0) {
    return 1
  }

  const distance = levenshteinDistance(normalizedA, normalizedB)
  return 1 - (distance / maxLength)
}

export interface FuzzyMatchCandidate<T> {
  result: T
  matchStrings: string[]
}

/**
 * Try to match a single string against candidates
 * Returns the match result and score, or null if no match above threshold
 */
function matchAgainstCandidates<T> (
  input: string,
  candidates: Array<FuzzyMatchCandidate<T>>,
  fuzzyThreshold: number
): { result: T, score: number } | null {
  const normalizedInput = normalizeString(input)
  let bestMatch: T | null = null
  let bestScore = 0

  for (const candidate of candidates) {
    for (const matchString of candidate.matchStrings) {
      if (normalizeString(matchString) === normalizedInput) {
        return { result: candidate.result, score: 1 }
      }
    }

    for (const matchString of candidate.matchStrings) {
      const similarity = stringSimilarity(input, matchString)
      if (similarity > bestScore) {
        bestScore = similarity
        bestMatch = candidate.result
      }
    }
  }

  if (bestScore >= fuzzyThreshold && bestMatch !== null) {
    return { result: bestMatch, score: bestScore }
  }

  return null
}

/**
 * Generic fuzzy match function - matches input against candidates
 * First tries to match the full input string, then falls back to matching
 * individual words within the input
 */
export function fuzzyMatch<T> (
  input: string,
  candidates: Array<FuzzyMatchCandidate<T>>,
  fuzzyThreshold: number = 0.7
): T | null {
  if (!input || typeof input !== 'string') {
    return null
  }

  // First, try matching the full input string
  const fullMatch = matchAgainstCandidates(input, candidates, fuzzyThreshold)
  if (fullMatch) {
    return fullMatch.result
  }

  // If no match, try matching individual words from the input
  const words = normalizeString(input).split(' ').filter(word => word.length > 0)
  let bestWordMatch: { result: T, score: number } | null = null

  for (const word of words) {
    const wordMatch = matchAgainstCandidates(word, candidates, fuzzyThreshold)
    if (wordMatch && (!bestWordMatch || wordMatch.score > bestWordMatch.score)) {
      bestWordMatch = wordMatch
    }
  }

  return bestWordMatch ? bestWordMatch.result : null
}
